import React, { useEffect, useRef, useState } from "react";
import { Tooltip } from "antd";
import moment from "moment";

export default function TimelineGantt({ tasks, visibleTaskIds, viewScale = 'month' }) {
  const containerRef = useRef(null);
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });
  const [hoveredTask, setHoveredTask] = useState(null);
  const [scrollLeft, setScrollLeft] = useState(0);
  
  // Constants for layout
  const ROW_HEIGHT = 45;
  const HEADER_HEIGHT = 50;
  const LEFT_MARGIN = 250; // Space for task names
  const MIN_TASK_WIDTH = 30;

  // Scale-dependent sizes
  const SCALE_CONFIG = {
    day: { unit: 'day', step: 1, columnWidth: 26 },
    week: { unit: 'week', step: 1, columnWidth: 60 },
    month: { unit: 'month', step: 1, columnWidth: 120 },
    year: { unit: 'year', step: 1, columnWidth: 220 }
  };
  const COLUMN_WIDTH = SCALE_CONFIG[viewScale]?.columnWidth || 120;

  useEffect(() => {
    const updateDimensions = () => {
      if (containerRef.current) {
        setDimensions({
          width: containerRef.current.offsetWidth,
          height: containerRef.current.offsetHeight
        });
      }
    };

    updateDimensions();
    window.addEventListener('resize', updateDimensions);
    return () => window.removeEventListener('resize', updateDimensions);
  }, []);

  // Filter tasks based on visibility
  const filteredTasks = tasks.filter(task => 
    !visibleTaskIds || visibleTaskIds.includes(task.id)
  );

  // Calculate date range
  const getDateRange = () => {
    if (filteredTasks.length === 0) {
      return {
        startDate: moment().startOf(viewScale === 'day' ? 'week' : viewScale),
        endDate: moment().add(3, viewScale === 'day' ? 'months' : viewScale).endOf(viewScale === 'day' ? 'month' : viewScale)
      };
    }

    let minDate = moment();
    let maxDate = moment();

    filteredTasks.forEach(task => {
      if (task.taskStartDate) {
        const startDate = moment(task.taskStartDate);
        if (startDate.isBefore(minDate)) minDate = startDate;
      }
      if (task.taskEndDate) {
        const endDate = moment(task.taskEndDate);
        if (endDate.isAfter(maxDate)) maxDate = endDate;
      }
    });

    // Add appropriate padding based on scale
    switch (viewScale) {
      case 'day':
        minDate.subtract(1, 'week').startOf('week');
        maxDate.add(1, 'week').endOf('week');
        break;
      case 'week':
        minDate.subtract(2, 'weeks').startOf('week');
        maxDate.add(2, 'weeks').endOf('week');
        break;
      case 'month':
        minDate.subtract(1, 'month').startOf('month');
        maxDate.add(1, 'month').endOf('month');
        break;
      case 'year':
        minDate.subtract(1, 'year').startOf('year');
        maxDate.add(1, 'year').endOf('year');
        break;
      default:
        minDate.subtract(1, 'month').startOf('month');
        maxDate.add(1, 'month').endOf('month');
    }

    return { startDate: minDate, endDate: maxDate };
  };

  const { startDate, endDate } = getDateRange();

  const getTotalColumns = () => {
    switch (viewScale) {
      case 'day':
        return endDate.diff(startDate, 'days') + 1;
      case 'week':
        return Math.ceil(endDate.diff(startDate, 'weeks', true));
      case 'month':
        return endDate.diff(startDate, 'months') + 1;
      case 'year':
        return endDate.diff(startDate, 'years') + 1;
      default:
        return endDate.diff(startDate, 'days') + 1;
    }
  };
  const totalColumns = getTotalColumns();
  const totalWidth = Math.max(dimensions.width - LEFT_MARGIN, totalColumns * COLUMN_WIDTH);

  // Build headers
  const getHeaders = () => {
    if (viewScale === 'day') {
      return getDayViewHeaders();
    }

    const segments = [];
    let current = moment(startDate).clone();

    if (viewScale === 'week') {
      current = current.startOf('week');
      while (current.isSameOrBefore(endDate, 'week')) {
        const weekStart = current.clone().startOf('week');
        const weekEnd = current.clone().endOf('week');
        const label = `${weekStart.format('DD MMM')} - ${weekEnd.format('DD MMM')}`;
        const startOffset = Math.floor(current.diff(startDate, 'weeks', true)) * COLUMN_WIDTH;
        segments.push({ label, start: startOffset, width: COLUMN_WIDTH });
        current.add(1, 'week');
      }
    } else if (viewScale === 'month') {
      current = current.startOf('month');
      while (current.isSameOrBefore(endDate, 'month')) {
        const label = current.format('MMM YYYY');
        const startOffset = current.diff(startDate, 'months') * COLUMN_WIDTH;
        segments.push({ label, start: startOffset, width: COLUMN_WIDTH });
        current.add(1, 'month');
      }
    } else if (viewScale === 'year') {
      current = current.startOf('year');
      while (current.isSameOrBefore(endDate, 'year')) {
        const label = current.format('YYYY');
        const startOffset = current.diff(startDate, 'years') * COLUMN_WIDTH;
        segments.push({ label, start: startOffset, width: COLUMN_WIDTH });
        current.add(1, 'year');
      }
    }

    return segments;
  };

  const getDayViewHeaders = () => {
    const monthSegments = [];
    const daySegments = [];

    let currentMonth = moment(startDate).clone().startOf('month');
    while (currentMonth.isSameOrBefore(endDate, 'month')) {
      const monthStart = moment.max(currentMonth, startDate);
      const monthEnd = moment.min(currentMonth.clone().endOf('month'), endDate);

      const startOffset = monthStart.diff(startDate, 'days') * COLUMN_WIDTH;
      const daysInThisSegment = monthEnd.diff(monthStart, 'days') + 1;
      const width = daysInThisSegment * COLUMN_WIDTH;

      monthSegments.push({
        label: currentMonth.format('MMM YYYY'),
        start: startOffset,
        width
      });

      currentMonth.add(1, 'month');
    }

    let currentDay = moment(startDate).clone();
    while (currentDay.isSameOrBefore(endDate, 'day')) {
      const startOffset = currentDay.diff(startDate, 'days') * COLUMN_WIDTH;
      daySegments.push({
        label: currentDay.format('D'),
        start: startOffset,
        width: COLUMN_WIDTH,
        isWeekend: currentDay.day() === 0 || currentDay.day() === 6,
        isToday: currentDay.isSame(moment(), 'day')
      });
      currentDay.add(1, 'day');
    }

    return { monthSegments, daySegments };
  };

  const getTaskPosition = (task) => {
    const start = task.taskStartDate ? moment(task.taskStartDate) : moment();
    const end = task.taskEndDate ? moment(task.taskEndDate) : moment().add(7, 'days');

    let leftUnits = 0;
    let spanUnits = 1;

    switch (viewScale) {
      case 'day':
        leftUnits = start.diff(startDate, 'days');
        spanUnits = Math.max(1, end.diff(start, 'days') + 1);
        break;
      case 'week':
        leftUnits = Math.floor(start.diff(startDate, 'weeks', true));
        spanUnits = Math.max(1, Math.ceil(end.diff(start, 'weeks', true)));
        break;
      case 'month':
        leftUnits = start.diff(startDate, 'months');
        spanUnits = Math.max(1, end.diff(start, 'months') + 1);
        break;
      case 'year':
        leftUnits = start.diff(startDate, 'years');
        spanUnits = Math.max(1, end.diff(start, 'years') + 1);
        break;
      default:
        leftUnits = start.diff(startDate, 'days');
        spanUnits = Math.max(1, end.diff(start, 'days') + 1);
    }

    const left = leftUnits * COLUMN_WIDTH;
    const width = Math.max(MIN_TASK_WIDTH, spanUnits * COLUMN_WIDTH);

    return { left, width };
  };

  const getTaskColor = (task) => {
    if (task.completionStatus) return '#52c41a'; // Green for completed
    const today = moment();
    const endDate = moment(task.taskEndDate);
    if (endDate.isBefore(today)) return '#ff4d4f'; // Red for overdue
    if (endDate.diff(today, 'days') <= 7) return '#faad14'; // Orange for due soon
    if (endDate.diff(today, 'days') <= 14) return '#1890ff'; // Blue for upcoming
    return '#722ed1'; // Purple for future tasks
  };

  const renderTask = (task, index) => {
    const { left, width } = getTaskPosition(task);
    const color = getTaskColor(task);
    const progress = task.completionStatus ? 100 : (task.progress || 0);
    const isHovered = hoveredTask?.id === task.id;

    return (
      <div
        key={task.id}
        className="gantt-task-row"
        style={{ position: 'absolute', top: HEADER_HEIGHT + (index * ROW_HEIGHT), height: ROW_HEIGHT, width: totalWidth + LEFT_MARGIN }}
      >
        {/* Highlight line that connects task name to timeline bar when hovered - only in task column */}
        {isHovered && (
          <div
            className="task-highlight-line"
            style={{
              position: 'absolute',
              left: 0,
              top: 0,
              width: LEFT_MARGIN - 15,
              height: ROW_HEIGHT,
              backgroundColor: 'rgba(0, 0, 0, 0.05)',
              borderTop: '2px solid #000000',
              borderBottom: '2px solid #000000',
              borderRight: '2px solid #000000',
              zIndex: 3,
              pointerEvents: 'none',
              animation: 'highlightSpread 0.3s ease-out'
            }}
          />
        )}

        <div
          className="task-name"
          style={{ 
            position: 'absolute', 
            left: 0, 
            width: LEFT_MARGIN - 15, 
            height: ROW_HEIGHT, 
            display: 'flex', 
            alignItems: 'center', 
            paddingLeft: 15, 
            paddingRight: 15, 
            borderRight: isHovered ? '3px solid #000000' : '2px solid #e8e8e8', 
            backgroundColor: isHovered ? '#f0f0f0' : (index % 2 === 0 ? '#fafafa' : '#ffffff'), 
            overflow: 'hidden', 
            textOverflow: 'ellipsis', 
            whiteSpace: 'nowrap', 
            zIndex: 5,
            borderBottom: '1px solid #f0f0f0',
            transition: 'all 0.3s ease',
            boxShadow: isHovered ? '0 2px 8px rgba(0,0,0,0.1)' : 'none'
          }}
        >
          <Tooltip title={`${task.name}${task.assignedUserName || (task.positions?.users && `${task.positions?.users?.firstName || ''} ${task.positions?.users?.lastName || ''}`.trim()) ? ` - Assigned to: ${task.assignedUserName || `${task.positions?.users?.firstName || ''} ${task.positions?.users?.lastName || ''}`.trim()}` : ''}`}>
            <div style={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
              <span className="text-sm font-medium" style={{ 
                color: isHovered ? '#000000' : '#262626', 
                marginBottom: '2px',
                fontWeight: isHovered ? 'bold' : 'normal',
                transition: 'all 0.3s ease'
              }}>{task.name}</span>
              {(task.assignedUserName || (task.positions?.users && `${task.positions?.users?.firstName || ''} ${task.positions?.users?.lastName || ''}`.trim())) && (
                <span className="text-xs" style={{ 
                  color: isHovered ? '#333333' : '#8c8c8c',
                  transition: 'all 0.3s ease'
                }}>
                  {task.assignedUserName || `${task.positions?.users?.firstName || ''} ${task.positions?.users?.lastName || ''}`.trim()}
                </span>
              )}
            </div>
          </Tooltip>
        </div>

        <div
          style={{ position: 'absolute', left: LEFT_MARGIN + left, top: (ROW_HEIGHT - 28) / 2, width: width, height: 28 }}
          onMouseEnter={(e) => {
            const rect = e.currentTarget.getBoundingClientRect();
            const containerRect = containerRef.current.getBoundingClientRect();
            const scrollableContent = containerRef.current.querySelector('.gantt-content');
            const scrollTop = scrollableContent ? scrollableContent.scrollTop : 0;
            setHoveredTask({ 
              id: task.id, 
              x: e.nativeEvent.offsetX,
              absoluteX: rect.left - containerRect.left + e.nativeEvent.offsetX,
              absoluteY: rect.top - containerRect.top - scrollTop,
              taskRowIndex: index
            });
          }}
          onMouseMove={(e) => {
            const rect = e.currentTarget.getBoundingClientRect();
            const containerRect = containerRef.current.getBoundingClientRect();
            const scrollableContent = containerRef.current.querySelector('.gantt-content');
            const scrollTop = scrollableContent ? scrollableContent.scrollTop : 0;
            setHoveredTask({ 
              id: task.id, 
              x: e.nativeEvent.offsetX,
              absoluteX: rect.left - containerRect.left + e.nativeEvent.offsetX,
              absoluteY: rect.top - containerRect.top - scrollTop,
              taskRowIndex: index
            });
          }}
          onMouseLeave={() => setHoveredTask(null)}
        >
          <div
            className="task-bar"
            style={{ 
              height: '100%', 
              backgroundColor: color + '15', 
              border: `2px solid ${color}`, 
              borderRadius: 6, 
              position: 'relative', 
              cursor: 'pointer', 
              transition: 'all 0.3s ease',
              boxShadow: isHovered ? `0 6px 16px ${color}50` : '0 2px 4px rgba(0,0,0,0.1)',
              transform: isHovered ? 'translateY(-2px) scale(1.02)' : 'translateY(0) scale(1)',
              zIndex: isHovered ? 10 : 1
            }}
          >
            <div style={{ 
              position: 'absolute', 
              left: 0, 
              top: 0, 
              bottom: 0, 
              width: `${progress}%`, 
              backgroundColor: color, 
              borderRadius: 4, 
              opacity: 0.6,
              transition: 'width 0.3s ease'
            }} />
            {width > 60 && (
              <div style={{ 
                position: 'absolute', 
                left: '50%', 
                top: '50%', 
                transform: 'translate(-50%, -50%)', 
                fontSize: 11, 
                fontWeight: 'bold', 
                color: progress > 50 ? '#ffffff' : color,
                textShadow: progress > 50 ? '0 1px 2px rgba(0,0,0,0.3)' : 'none'
              }}>{progress}%</div>
            )}
          </div>
        </div>
      </div>
    );
  };

  const headers = getHeaders();

  return (
    <div ref={containerRef} className="gantt-container" style={{ position: 'relative', width: '100%', height: '100%', backgroundColor: 'white', border: '1px solid #e8e8e8', borderRadius: 4, overflow: 'hidden' }}>
      {/* CSS Animations */}
      <style jsx>{`
        @keyframes highlightSpread {
          0% {
            opacity: 0;
            transform: scaleX(0);
          }
          100% {
            opacity: 1;
            transform: scaleX(1);
          }
        }
        
        .task-highlight-line {
          transform-origin: left center;
        }
      `}</style>

      {/* Tooltip positioned absolutely in container */}
      {hoveredTask && (
        <div
          className="gantt-tooltip"
          style={{
            position: 'absolute',
            top: Math.max(HEADER_HEIGHT + 10, hoveredTask.absoluteY - 100), // Position above the task, but not above header
            left: Math.min(Math.max(hoveredTask.absoluteX - 110, LEFT_MARGIN + 10), dimensions.width - 230),
            backgroundColor: 'rgba(0, 0, 0, 0.92)',
            color: 'white',
            padding: '14px 18px',
            borderRadius: 10,
            fontSize: 13,
            whiteSpace: 'nowrap',
            zIndex: 2000,
            pointerEvents: 'none',
            boxShadow: '0 12px 32px rgba(0,0,0,0.4)',
            border: `2px solid ${getTaskColor(filteredTasks.find(t => t.id === hoveredTask.id))}`,
            minWidth: '220px',
            backdropFilter: 'blur(8px)',
            opacity: 1,
            transition: 'opacity 0.15s ease-in-out'
          }}
        >
          {(() => {
            const task = filteredTasks.find(t => t.id === hoveredTask.id);
            if (!task) return null;
            const color = getTaskColor(task);
            const progress = task.completionStatus ? 100 : (task.progress || 0);
            
            return (
              <>
                <div style={{ fontWeight: 'bold', marginBottom: '8px', fontSize: '15px', color: '#ffffff' }}>{task.name}</div>
                <div style={{ marginBottom: '5px', opacity: 0.95 }}>
                  Status: <span style={{ color: color, fontWeight: 'bold' }}>{task.status || 'Active'}</span>
                </div>
                <div style={{ marginBottom: '5px', opacity: 0.95 }}>
                  Progress: <span style={{ color: color, fontWeight: 'bold' }}>{progress}%</span>
                </div>
                {(task.assignedUserName || (task.positions?.users && `${task.positions?.users?.firstName || ''} ${task.positions?.users?.lastName || ''}`.trim())) && (
                  <div style={{ marginBottom: '5px', opacity: 0.95 }}>
                    Assigned to: <span style={{ color: '#87d068', fontWeight: 'bold' }}>
                      {task.assignedUserName || `${task.positions?.users?.firstName || ''} ${task.positions?.users?.lastName || ''}`.trim()}
                    </span>
                  </div>
                )}
                <div style={{ 
                  opacity: 0.85, 
                  fontSize: '12px', 
                  borderTop: '1px solid rgba(255,255,255,0.3)', 
                  paddingTop: '8px', 
                  marginTop: '8px',
                  color: '#e6e6e6'
                }}>
                  {task.taskStartDate && moment(task.taskStartDate).isValid() ? `${moment(task.taskStartDate).format('MMM DD, YYYY')} → ` : ''}{task.taskEndDate && moment(task.taskEndDate).isValid() ? moment(task.taskEndDate).format('MMM DD, YYYY') : 'No target date'}
                </div>
              </>
            );
          })()}
          
          {/* Arrow pointer pointing down to the task */}
          <div
            style={{
              position: 'absolute',
              bottom: -8,
              left: '50%',
              transform: 'translateX(-50%)',
              width: 0,
              height: 0,
              borderLeft: '8px solid transparent',
              borderRight: '8px solid transparent',
              borderTop: `8px solid ${getTaskColor(filteredTasks.find(t => t.id === hoveredTask.id))}`,
              zIndex: 2001
            }}
          />
        </div>
      )}

      {/* Additional CSS for tooltip animation */}
      <style jsx>{`
        .gantt-tooltip {
          animation: tooltipFadeIn 0.15s ease-out;
        }
        
        @keyframes tooltipFadeIn {
          0% {
            opacity: 0;
            transform: translateY(5px);
          }
          100% {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>

      {/* Header */}
      {viewScale === 'day' ? (
        <>
          <div style={{ position: 'absolute', top: 0, left: -scrollLeft, right: 0, height: HEADER_HEIGHT, zIndex: 10 }}>
            <div style={{ position: 'absolute', left: 0, top: 0, width: LEFT_MARGIN - 1, height: HEADER_HEIGHT, borderRight: '1px solid #e8e8e8', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 'bold', backgroundColor: '#f0f0f0' }}>Tasks</div>
          </div>
          {/* Month row */}
          <div style={{ position: 'absolute', left: LEFT_MARGIN - scrollLeft, top: 0, height: HEADER_HEIGHT / 2, width: totalWidth, borderBottom: '1px solid #e8e8e8' }}>
            {headers.monthSegments?.map((seg, i) => (
              <div key={`m-${i}`} style={{ position: 'absolute', left: seg.start, width: seg.width, height: '100%', borderRight: '1px solid #e8e8e8', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 'bold', fontSize: 13, backgroundColor: '#f8f9fa' }}>{seg.label}</div>
            ))}
          </div>
          {/* Day row */}
          <div style={{ position: 'absolute', left: LEFT_MARGIN - scrollLeft, top: HEADER_HEIGHT / 2, height: HEADER_HEIGHT / 2, width: totalWidth, borderBottom: '1px solid #e8e8e8' }}>
            {headers.daySegments?.map((seg, i) => (
              <div key={`d-${i}`} style={{ position: 'absolute', left: seg.start, width: seg.width, height: '100%', borderRight: '1px solid #f0f0f0', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, backgroundColor: seg.isToday ? '#e6f7ff' : seg.isWeekend ? '#f5f5f5' : 'transparent', fontWeight: seg.isToday ? 'bold' : 'normal' }}>{seg.label}</div>
            ))}
          </div>
        </>
      ) : (
        <div className="gantt-header" style={{ position: 'absolute', top: 0, left: -scrollLeft, right: 0, height: HEADER_HEIGHT, borderBottom: '2px solid #e8e8e8', backgroundColor: '#fafafa', zIndex: 10 }}>
          <div style={{ position: 'absolute', left: 0, top: 0, width: LEFT_MARGIN - 1, height: HEADER_HEIGHT, borderRight: '1px solid #e8e8e8', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 'bold', backgroundColor: '#f0f0f0' }}>Tasks</div>
          <div style={{ position: 'absolute', left: LEFT_MARGIN, top: 0, height: HEADER_HEIGHT, width: totalWidth, borderBottom: '1px solid #e8e8e8' }}>
            {headers.map((seg, index) => (
              <div key={index} style={{ position: 'absolute', left: seg.start, width: seg.width, height: '100%', borderRight: '1px solid #e8e8e8', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 'bold', fontSize: 14 }}>
                {seg.label}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Scrollable content */}
      <div 
        className="gantt-content" 
        style={{ position: 'absolute', top: HEADER_HEIGHT, left: 0, right: 0, bottom: 0, overflowY: 'auto', overflowX: 'auto', scrollbarWidth: 'thin' }}
        onScroll={(e) => setScrollLeft(e.target.scrollLeft)}
      >
        {/* Grid vertical columns - positioned to not overlap task names */}
        <div style={{ position: 'absolute', top: 0, left: LEFT_MARGIN, width: totalWidth, height: filteredTasks.length * ROW_HEIGHT, pointerEvents: 'none', zIndex: 1 }}>
          {Array.from({ length: totalColumns }).map((_, colIndex) => {
            let isWeekend = false; let isToday = false;
            if (viewScale === 'day') {
              const currentDate = moment(startDate).add(colIndex, 'days');
              isWeekend = currentDate.day() === 0 || currentDate.day() === 6;
              isToday = currentDate.isSame(moment(), 'day');
            }
            return (
              <div key={colIndex} style={{ position: 'absolute', left: colIndex * COLUMN_WIDTH, top: 0, width: COLUMN_WIDTH, height: '100%', borderRight: '1px solid #f0f0f0', backgroundColor: isToday ? '#e6f7ff20' : isWeekend ? '#f5f5f520' : 'transparent' }} />
            );
          })}
        </div>

        {/* Task rows */}
        {filteredTasks.map((task, index) => renderTask(task, index))}

        {/* Row separators and alternating backgrounds */}
        {filteredTasks.map((_, index) => (
          <div key={`row-bg-${index}`} style={{ 
            position: 'absolute', 
            top: index * ROW_HEIGHT, 
            left: LEFT_MARGIN, 
            width: totalWidth, 
            height: ROW_HEIGHT, 
            backgroundColor: index % 2 === 0 ? '#fafafa08' : 'transparent',
            borderBottom: '1px solid #f0f0f0',
            pointerEvents: 'none'
          }} />
        ))}
      </div>
    </div>
  );
}

